import { useRef, useState } from 'react';
import toast from 'react-hot-toast';

import { IconClose, IconPlus, IconTrash } from '../../../common/icons';
import { useModalDialog } from '../../../common/hooks/use-modal-dialog';
import { useRacesList } from '../../races/contexts/races';
import { usePregnancy } from '../../pregnancies/contexts/pregnancies';
import { useCreateBirth } from '../contexts/births';

import '../../../common/styles/catalog.css';
import './styles/form.css';

const today = () => new Date().toLocaleDateString('en-CA');
const EMPTY_CALF = { sex: '', weight: '', type: '', baptized: true, name: '', number: '', register: '', race_id: '' };

// Bloque de una cría. La raza es OPCIONAL (el sistema la calcula con calculateBreed
// si se deja en "Auto"). El sexo es lo único requerido; bautizada → nombre+número.
function CalfBlock({ calf, index, races, errors, onChange, onRemove, removable, disabled }) {
    const baptized = calf.baptized !== false;
    // errores y handlers por id ESTABLE (no por índice) → quitar una cría no corre los errores.
    const err = (f) => errors[`${calf._id}.${f}`];
    const set = (f, v) => onChange(calf._id, { [f]: v });
    return (
        <div className="birth-calf">
            <div className="birth-calf__head">
                <span className="birth-calf__title">Cría {index + 1}</span>
                {removable && (
                    <button type="button" className="birth-calf__remove" onClick={() => onRemove(calf._id)} disabled={disabled} aria-label="Quitar cría" title="Quitar cría"><IconTrash /></button>
                )}
            </div>
            <div className="birth-modal__grid">
                <label className="cat-form__field">
                    <span className="cat-form__label">Sexo</span>
                    <select value={calf.sex} onChange={(e) => set('sex', e.target.value)} disabled={disabled} aria-invalid={!!err('sex')}>
                        <option value="">Seleccionar…</option>
                        <option value="female">Hembra</option>
                        <option value="male">Macho</option>
                    </select>
                    {err('sex') && <span className="cat-form__error" role="alert">{err('sex')}</span>}
                </label>
                <label className="cat-form__field">
                    <span className="cat-form__label">Peso (kg)</span>
                    <input type="number" min="0" step="0.1" value={calf.weight} onChange={(e) => set('weight', e.target.value)} disabled={disabled} placeholder="Opcional" />
                </label>

                <div className="cat-form__field birth-modal__field--full">
                    <span className="cat-form__label">Bautizo</span>
                    <div className="cat-form__toggle" role="group" aria-label="Bautizo">
                        <button type="button" className={`cat-form__toggle-btn ${baptized ? 'cat-form__toggle-btn--active' : ''}`} onClick={() => set('baptized', true)} disabled={disabled}>Con bautizo</button>
                        <button type="button" className={`cat-form__toggle-btn ${!baptized ? 'cat-form__toggle-btn--active' : ''}`} onClick={() => set('baptized', false)} disabled={disabled}>Sin bautizo</button>
                    </div>
                    {!baptized && <span className="cat-form__hint">Cría pendiente: nombre/número opcionales; el sistema le pone un nombre provisional.</span>}
                </div>

                <label className="cat-form__field">
                    <span className="cat-form__label">Nombre{baptized ? '' : ' (opcional)'}</span>
                    <input type="text" value={calf.name} maxLength={120} onChange={(e) => set('name', e.target.value)} disabled={disabled} aria-invalid={!!err('name')} />
                    {err('name') && <span className="cat-form__error" role="alert">{err('name')}</span>}
                </label>
                <label className="cat-form__field">
                    <span className="cat-form__label">Número{baptized ? '' : ' (opcional)'}</span>
                    <input type="text" value={calf.number} maxLength={40} onChange={(e) => set('number', e.target.value)} disabled={disabled} aria-invalid={!!err('number')} />
                    {err('number') && <span className="cat-form__error" role="alert">{err('number')}</span>}
                </label>
                <label className="cat-form__field">
                    <span className="cat-form__label">Registro</span>
                    <input type="text" value={calf.register} maxLength={60} onChange={(e) => set('register', e.target.value)} disabled={disabled} placeholder="Opcional · único" />
                </label>
                <label className="cat-form__field">
                    <span className="cat-form__label">Raza</span>
                    <select value={calf.race_id} onChange={(e) => set('race_id', e.target.value)} disabled={disabled}>
                        <option value="">Auto (calculada)</option>
                        {races.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                </label>
                <label className="cat-form__field">
                    <span className="cat-form__label">Tipo de parto</span>
                    <input type="text" value={calf.type} maxLength={40} onChange={(e) => set('type', e.target.value)} disabled={disabled} placeholder="Normal, cesárea… (opcional)" />
                </label>
            </div>
        </div>
    );
}

// Formulario de parto (admin-births.md): crea cría(s) + nacimiento(s) + genealogía y
// pasa la preñez a `born`. Soporta GEMELOS (0037): una camada va como `calves: [...]`;
// una sola cría como `calf` (backward-compatible). Muestra el contexto del "cruce
// completo" (0038): padres (madre×toro), finca y ticket ASOCEBU, vía GET /pregnancies/:id.
export default function PartoForm({ pregnancyId, recipientLabel, defaultFarmId, onClose, onSaved }) {
    const { mutateAsync, isPending } = useCreateBirth();
    const cardRef = useModalDialog(onClose);
    const { data: racesData } = useRacesList({});
    const races = racesData?.items ?? [];
    const { data: preg } = usePregnancy(pregnancyId);

    const t = preg?.transfer || {};
    const e = t.embryo || {};
    const donor = e.donor;       // madre
    const sire = e.sire;         // padre
    const farm = t.recipient_farm;
    const ticket = preg?.asocebu_ticket;
    const recipient = t.recipient;
    const farmId = farm?.id ?? defaultFarmId;
    const recLabel = recipientLabel || (recipient ? `${recipient.number || ''}${recipient.name ? ` · ${recipient.name}` : ''}`.trim() : '');

    const [date, setDate] = useState(today());
    const [remarks, setRemarks] = useState('');
    const idRef = useRef(1);
    const [calves, setCalves] = useState(() => [{ _id: 0, ...EMPTY_CALF }]); // id estable por cría
    const [errors, setErrors] = useState({});

    const setCalf = (id, patch) => {
        setCalves((cs) => cs.map((c) => (c._id === id ? { ...c, ...patch } : c)));
        setErrors((er) => { const n = { ...er }; Object.keys(patch).forEach((f) => delete n[`${id}.${f}`]); return n; });
    };
    const addCalf = () => setCalves((cs) => [...cs, { _id: idRef.current++, ...EMPTY_CALF }]);
    const removeCalf = (id) => {
        setCalves((cs) => (cs.length > 1 ? cs.filter((c) => c._id !== id) : cs));
        setErrors((er) => { const n = { ...er }; Object.keys(n).forEach((k) => { if (k.startsWith(`${id}.`)) delete n[k]; }); return n; });
    };

    const buildCalf = (c) => {
        const calf = { baptized: c.baptized !== false };
        if (c.name.trim()) calf.name = c.name.trim();
        if (c.number.trim()) calf.number = c.number.trim();
        if (c.register.trim()) calf.register = c.register.trim();
        if (c.race_id) calf.race_id = Number(c.race_id);
        return calf;
    };

    const submit = async (ev) => {
        ev.preventDefault();
        const errs = {};
        if (!date) errs.date = 'Indicá la fecha.';
        calves.forEach((c) => {
            if (!c.sex) errs[`${c._id}.sex`] = 'Elegí el sexo.';
            if (c.baptized !== false) {
                if (!c.name.trim()) errs[`${c._id}.name`] = 'Nombre requerido.';
                if (!c.number.trim()) errs[`${c._id}.number`] = 'Número requerido.';
            }
        });
        if (Object.keys(errs).length) { setErrors(errs); toast.error('Revisá los campos'); return; }
        setErrors({});

        const payload = { pregnancy_id: pregnancyId, date };
        if (farmId) payload.farm_id = Number(farmId);
        if (remarks.trim()) payload.remarks = remarks.trim();

        if (calves.length === 1) {
            const c = calves[0];
            payload.sex = c.sex;
            payload.calf = buildCalf(c);
            if (c.weight !== '') payload.weight = Number(c.weight);
            if (c.type.trim()) payload.type = c.type.trim();
        } else {
            payload.calves = calves.map((c) => {
                const entry = { sex: c.sex, calf: buildCalf(c) };
                if (c.weight !== '') entry.weight = Number(c.weight);
                if (c.type.trim()) entry.type = c.type.trim();
                return entry;
            });
        }

        try {
            const res = await mutateAsync(payload);
            const n = res?.count || 1;
            toast.success(n > 1 ? `${n} crías registradas` : 'Parto registrado');
            onSaved?.();
            onClose();
        } catch (er) {
            if (er?.status === 409) toast.error('La preñez está perdida, o el registro de una cría ya existe');
            else if (er?.status === 422) toast.error('Raza indeterminada: elegí la raza de la cría');
            else if (er?.fields) { const msgs = Object.values(er.fields).filter(Boolean).join(' · '); toast.error(msgs || 'Revisá los campos'); }
            else if (er?.status !== 403) toast.error(er?.message || 'No se pudo registrar el parto');
        }
    };

    const canSubmit = !isPending && !!date && calves.every((c) => c.sex && (c.baptized === false || (c.name.trim() && c.number.trim())));
    const twins = calves.length > 1;

    return (
        <div className="birth-modal" role="dialog" aria-modal="true" aria-label="Registrar parto">
            <div className="birth-modal__overlay" onClick={onClose} aria-hidden="true" />
            <form className="birth-modal__card birth-modal__card--wide" ref={cardRef} onSubmit={submit}>
                <header className="birth-modal__head">
                    <div>
                        <h2 className="birth-modal__title">Registrar parto</h2>
                        {recLabel && <span className="birth-modal__sub">Receptora {recLabel}</span>}
                    </div>
                    <button type="button" className="birth-modal__close" onClick={onClose} aria-label="Cerrar"><IconClose /></button>
                </header>

                {/* Cruce completo (0038): padres, finca y ticket ASOCEBU como contexto. */}
                <div className="birth-ctx">
                    <div className="birth-ctx__item">
                        <span className="birth-ctx__label">Madre (donante)</span>
                        <span className="birth-ctx__value">{donor ? `${donor.name || '—'}${donor.number ? ` · ${donor.number}` : ''}` : '—'}</span>
                    </div>
                    <div className="birth-ctx__item">
                        <span className="birth-ctx__label">Padre (toro)</span>
                        <span className="birth-ctx__value">{sire ? `${sire.name || '—'}${sire.register ? ` · ${sire.register}` : ''}` : '—'}</span>
                    </div>
                    <div className="birth-ctx__item">
                        <span className="birth-ctx__label">Finca</span>
                        <span className="birth-ctx__value">{farm?.name || '—'}</span>
                    </div>
                    <div className="birth-ctx__item">
                        <span className="birth-ctx__label">Ticket ASOCEBU</span>
                        <span className="birth-ctx__value">{ticket?.ticket || <span className="birth-ctx__muted">Sin ticket</span>}</span>
                    </div>
                </div>

                <div className="birth-modal__grid">
                    <label className="cat-form__field">
                        <span className="cat-form__label">Fecha del parto</span>
                        <input type="date" value={date} max={today()} onChange={(e) => setDate(e.target.value)} disabled={isPending} aria-invalid={!!errors.date} />
                        {errors.date && <span className="cat-form__error" role="alert">{errors.date}</span>}
                    </label>
                </div>

                {calves.map((c, i) => (
                    <CalfBlock key={c._id} calf={c} index={i} races={races} errors={errors} onChange={setCalf} onRemove={removeCalf} removable={twins} disabled={isPending} />
                ))}

                <button type="button" className="birth-modal__addcalf" onClick={addCalf} disabled={isPending}>
                    <IconPlus /> Agregar cría (gemelo)
                </button>

                <label className="cat-form__field">
                    <span className="cat-form__label">Notas</span>
                    <textarea value={remarks} rows={2} onChange={(e) => setRemarks(e.target.value)} disabled={isPending} placeholder="Opcional" />
                </label>

                <footer className="birth-modal__actions">
                    <button type="button" className="birth-modal__cancel" onClick={onClose} disabled={isPending}>Cancelar</button>
                    <button type="submit" className="birth-modal__submit" disabled={!canSubmit}>
                        {isPending ? 'Registrando…' : (twins ? `Registrar parto (${calves.length} crías)` : 'Registrar parto')}
                    </button>
                </footer>
            </form>
        </div>
    );
}
